import { Input, Select } from "@/components/base/Field";
import type { SchoolInfo } from "@/types";
import { CURRENCIES } from "@/utils/currency";
import { montantEnLettres } from "@/utils/receipt";

interface Props {
  info: SchoolInfo;
  onChange: (patch: Partial<SchoolInfo>) => void;
}

const OTHER = "__autre__";

/** Choix de la devise de l'école : simple affichage, les montants enregistrés ne sont pas convertis. */
export default function CurrencyFields({ info, onChange }: Props) {
  const known = CURRENCIES.find(
    (c) => c.symbol === info.currencySymbol && c.name === info.currencyName && c.plural === info.currencyNamePlural,
  );
  const selected = known ? known.symbol + "|" + known.name : OTHER;
  const example = 1_250_000;
  const grouped = example.toLocaleString("fr-FR").replace(/[  ]/g, " ");

  return (
    <div className="mt-6">
      <p className="mb-3 text-sm font-medium text-foreground-900">Devise</p>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Select
          label="Devise de l'école"
          value={selected}
          onChange={(e) => {
            if (e.target.value === OTHER) {
              onChange({ currencySymbol: "", currencyName: "", currencyNamePlural: "" });
              return;
            }
            const c = CURRENCIES.find((x) => x.symbol + "|" + x.name === e.target.value)!;
            onChange({ currencySymbol: c.symbol, currencyName: c.name, currencyNamePlural: c.plural });
          }}
          wrapperClass="sm:col-span-2"
        >
          {CURRENCIES.map((c) => (
            <option key={c.symbol + c.name} value={c.symbol + "|" + c.name}>
              {c.label}
            </option>
          ))}
          <option value={OTHER}>Autre devise…</option>
        </Select>
        {selected === OTHER && (
          <>
            <Input
              label="Symbole affiché"
              placeholder="Ex. : Le"
              maxLength={12}
              value={info.currencySymbol}
              onChange={(e) => onChange({ currencySymbol: e.target.value })}
            />
            <Input
              label="Nom au singulier"
              placeholder="Ex. : leone"
              maxLength={60}
              value={info.currencyName}
              onChange={(e) => onChange({ currencyName: e.target.value })}
            />
            <Input
              label="Nom au pluriel"
              placeholder="Ex. : leones"
              maxLength={60}
              value={info.currencyNamePlural}
              onChange={(e) => onChange({ currencyNamePlural: e.target.value })}
              wrapperClass="sm:col-span-2"
            />
          </>
        )}
      </div>
      <p className="mt-3 rounded-md bg-background-100 px-3 py-2 text-xs text-foreground-600">
        Aperçu : <strong>{grouped} {info.currencySymbol || "?"}</strong> — en lettres sur les reçus : «{" "}
        {info.currencyName && info.currencyNamePlural
          ? montantEnLettres(example, { symbol: info.currencySymbol, name: info.currencyName, plural: info.currencyNamePlural })
          : "…"} ». Changer de devise ne convertit pas
        les montants déjà enregistrés : seul l'affichage change, partout dans l'application et sur les documents.
      </p>
    </div>
  );
}

